import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
  PieChart, Pie, LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  ArrowLeft, Download, FileSpreadsheet, Users, Pill, Calendar, 
  Building2, FileText, Filter, Search, RefreshCw, BarChart3, TestTube, TrendingUp, Image
} from 'lucide-react';
import { toast } from 'sonner';
import InteractiveChart from '../components/InteractiveChart';
import ExamStatistics from '../components/ExamStatistics';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

const AGE_COLORS = ['#0D9488', '#8B5CF6', '#E9604A', '#F59E0B', '#10B981', '#3B82F6', '#EC4899', '#6366F1', '#14B8A6', '#F97316'];
const CHART_COLORS = ['#0F766E', '#5FB3A2', '#1E3A8A', '#D97706', '#B91C1C', '#7C3AED', '#059669', '#DC2626'];

export default function Reports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Refs para os gráficos
  const ageChartRef = useRef(null);
  const medsChartRef = useRef(null);
  const monthChartRef = useRef(null);
  const pieChartRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [healthUnits, setHealthUnits] = useState([]);
  const [deliveryDates, setDeliveryDates] = useState([]);
  const [filters, setFilters] = useState({
    unit_id: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [medicationsByAge, setMedicationsByAge] = useState(null);
  const [selectedAgeRange, setSelectedAgeRange] = useState('0-12');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const [quickPrescriptionStats, setQuickPrescriptionStats] = useState(null);
  const [combinedMedsStats, setCombinedMedsStats] = useState(null);
  const [examsStats, setExamsStats] = useState(null);

  useEffect(() => {
    if (currentUser.role !== 'administrador' && currentUser.role !== 'auditor') {
      toast.error('Sem permissão para acessar relatórios');
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, patientsRes, unitsRes, medsAgeRes, prescRes, deliveryRes, quickStatsRes, combinedMedsRes, examsRes] = await Promise.all([
        axiosInstance.get('/admin/dashboard-stats'),
        axiosInstance.get('/patients'),
        axiosInstance.get('/health-units'),
        axiosInstance.get('/reports/medications-by-age'),
        axiosInstance.get('/prescriptions'),
        axiosInstance.get('/delivery-dates/all'),
        axiosInstance.get('/quick-prescriptions/stats').catch(() => ({ data: null })),
        axiosInstance.get('/reports/combined-medications').catch(() => ({ data: null })),
        axiosInstance.get('/reports/exams-stats').catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setPatients(patientsRes.data);
      setHealthUnits(unitsRes.data);
      setMedicationsByAge(medsAgeRes.data);
      setPrescriptions(prescRes.data || []);
      setDeliveryDates(deliveryRes.data || []);
      setQuickPrescriptionStats(quickStatsRes.data);
      setCombinedMedsStats(combinedMedsRes.data);
      setExamsStats(examsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar relatórios');
      setLoading(false);
    }
  };

  // Função para normalizar texto (remover acentos)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  // Filter patients based on filters
  const filteredPatients = patients.filter(patient => {
    if (filters.unit_id && patient.unit_id !== filters.unit_id) return false;
    if (filters.search) {
      const normalizedName = normalizeText(patient.nome);
      const normalizedSearch = normalizeText(filters.search);
      if (!normalizedName.includes(normalizedSearch)) return false;
    }
    return true;
  });

  // Calculate age from date of birth
  const calculateAge = (birthDate) => {
    if (!birthDate || birthDate === 'N/A' || birthDate === '01/01/1970') return null;
    
    // Parse different date formats
    let date = null;
    try {
      if (typeof birthDate === 'string' && birthDate.includes('/')) {
        const parts = birthDate.split('/');
        if (parts.length === 3) {
          // DD/MM/YYYY format
          date = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      } else if (birthDate) {
        date = new Date(birthDate);
      }
      
      // Validate the date
      if (!date || isNaN(date.getTime())) return null;
      
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age--;
      }
      return age >= 0 ? age : null;
    } catch (error) {
      console.error('Error calculating age:', error);
      return null;
    }
  };

  // Calculate patients by age group (intervals of 10 years)
  const getAgeGroupData = () => {
    const ageGroups = {
      '0-9': 0,
      '10-19': 0,
      '20-29': 0,
      '30-39': 0,
      '40-49': 0,
      '50-59': 0,
      '60-69': 0,
      '70-79': 0,
      '80-89': 0,
      '90+': 0,
      'Não informado': 0
    };

    patients.forEach(patient => {
      const age = calculateAge(patient.data_nascimento);
      if (age === null) {
        ageGroups['Não informado']++;
      } else if (age < 10) {
        ageGroups['0-9']++;
      } else if (age < 20) {
        ageGroups['10-19']++;
      } else if (age < 30) {
        ageGroups['20-29']++;
      } else if (age < 40) {
        ageGroups['30-39']++;
      } else if (age < 50) {
        ageGroups['40-49']++;
      } else if (age < 60) {
        ageGroups['50-59']++;
      } else if (age < 70) {
        ageGroups['60-69']++;
      } else if (age < 80) {
        ageGroups['70-79']++;
      } else if (age < 90) {
        ageGroups['80-89']++;
      } else {
        ageGroups['90+']++;
      }
    });

    return Object.entries(ageGroups)
      .filter(([_, count]) => count > 0)
      .map(([faixa, quantidade]) => ({ faixa, quantidade }));
  };

  const ageGroupData = getAgeGroupData();

  // Get medications data for selected age range
  const getMedicationsByAgeData = () => {
    if (!medicationsByAge || !medicationsByAge.medications_by_age) return [];
    return medicationsByAge.medications_by_age[selectedAgeRange] || [];
  };

  const medicationsByAgeData = getMedicationsByAgeData();

  // Export functions
  const exportToCSV = (data, filename, headers) => {
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h.toLowerCase().replace(/ /g, '_')] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportPatientsToCSV = () => {
    const data = filteredPatients.map(p => ({
      nome: p.nome,
      data_nascimento: p.data_nascimento || 'N/A',
      unidade: healthUnits.find(u => u.id === p.unit_id)?.nome || 'Sem unidade',
      data_cadastro: new Date(p.data_cadastro).toLocaleDateString('pt-BR')
    }));
    exportToCSV(data, 'pacientes', ['Nome', 'Data_Nascimento', 'Unidade', 'Data_Cadastro']);
    toast.success('Relatório de pacientes exportado!');
  };

  const exportMedicationsToCSV = () => {
    const data = (stats?.all_medications_list || []).map(m => ({
      medicamento: m.name,
      quantidade: m.count
    }));
    exportToCSV(data, 'medicamentos', ['Medicamento', 'Quantidade']);
    toast.success('Relatório de medicamentos exportado!');
  };

  const exportPatientsByUnitToCSV = () => {
    const data = (stats?.patients_by_unit || []).map(u => ({
      unidade: u.unit_name,
      pacientes: u.count
    }));
    exportToCSV(data, 'pacientes_por_unidade', ['Unidade', 'Pacientes']);
    toast.success('Relatório por unidade exportado!');
  };

  const exportCompleteReport = () => {
    // Create complete report with multiple sections
    let csvContent = '\ufeff'; // BOM for Excel UTF-8
    
    // Header
    csvContent += 'RELATÓRIO COMPLETO DO SISTEMA DE RECEITAS MÉDICAS\n';
    csvContent += `Data de geração: ${new Date().toLocaleString('pt-BR')}\n\n`;
    
    // Statistics
    csvContent += 'ESTATÍSTICAS GERAIS\n';
    csvContent += `Total de Pacientes,${stats?.total_patients || 0}\n`;
    csvContent += `Receitas Este Mês,${stats?.prescriptions_this_month || 0}\n`;
    csvContent += `Receitas a Vencer (30 dias),${stats?.expiring_soon || 0}\n`;
    csvContent += `Receitas Expiradas,${stats?.expired_prescriptions || 0}\n\n`;
    
    // Patients by unit
    csvContent += 'PACIENTES POR UNIDADE\n';
    csvContent += 'Unidade,Quantidade\n';
    (stats?.patients_by_unit || []).forEach(u => {
      csvContent += `"${u.unit_name}",${u.count}\n`;
    });
    csvContent += '\n';
    
    // Top medications
    csvContent += 'MEDICAMENTOS MAIS PRESCRITOS\n';
    csvContent += 'Medicamento,Quantidade\n';
    (stats?.top_medications_with_dosage || []).forEach(m => {
      csvContent += `"${m.name}",${m.count}\n`;
    });
    csvContent += '\n';
    
    // Patient list
    csvContent += 'LISTA DE PACIENTES\n';
    csvContent += 'Nome,Data Nascimento,Unidade,Data Cadastro\n';
    filteredPatients.forEach(p => {
      const unitName = healthUnits.find(u => u.id === p.unit_id)?.nome || 'Sem unidade';
      csvContent += `"${p.nome}","${p.data_nascimento || 'N/A'}","${unitName}","${new Date(p.data_cadastro).toLocaleDateString('pt-BR')}"\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `relatorio_completo_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Relatório completo exportado!');
  };

  // Função para capturar gráfico como imagem base64
  const captureChartAsImage = async (ref) => {
    if (!ref?.current) return null;
    try {
      const canvas = await html2canvas(ref.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      return canvas.toDataURL('image/png').split(',')[1]; // Retorna base64 sem prefixo
    } catch (error) {
      console.error('Erro ao capturar gráfico:', error);
      return null;
    }
  };

  // Exportar para Excel COM gráficos
  const exportToExcelWithCharts = async () => {
    setExporting(true);
    toast.info('Capturando gráficos... Aguarde.');
    
    try {
      const workbook = XLSX.utils.book_new();
      
      // Aba 1: Estatísticas Gerais
      const statsData = [
        ['RELATÓRIO DO SISTEMA DE RECEITAS MÉDICAS', ''],
        ['Fundo Municipal de Saúde de São Domingos do Prata', ''],
        ['', ''],
        ['Data de Geração', new Date().toLocaleString('pt-BR')],
        ['', ''],
        ['ESTATÍSTICAS GERAIS', ''],
        ['Total de Pacientes', stats?.total_patients || 0],
        ['Total de Receitas', prescriptions.length || 0],
        ['Receitas Este Mês', stats?.prescriptions_this_month || 0],
        ['Receitas a Vencer (30 dias)', stats?.expiring_soon || 0],
        ['Receitas Expiradas', stats?.expired_prescriptions || 0],
        ['Total de Unidades', healthUnits.length || 0],
        ['Total de Medicamentos Únicos', stats?.total_unique_medications || 0],
      ];
      const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
      // Ajustar largura das colunas
      statsSheet['!cols'] = [{ wch: 35 }, { wch: 25 }];
      XLSX.utils.book_append_sheet(workbook, statsSheet, 'Resumo');
      
      // Aba 2: Pacientes
      const patientsData = filteredPatients.map(p => ({
        'Nome': p.nome,
        'Data Nascimento': p.data_nascimento || 'N/A',
        'Idade': calculateAge(p.data_nascimento) || 'N/A',
        'Unidade': healthUnits.find(u => u.id === p.unit_id)?.nome || 'Sem unidade',
        'Data Cadastro': new Date(p.data_cadastro).toLocaleDateString('pt-BR')
      }));
      const patientsSheet = XLSX.utils.json_to_sheet(patientsData);
      patientsSheet['!cols'] = [{ wch: 40 }, { wch: 15 }, { wch: 10 }, { wch: 40 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, patientsSheet, 'Pacientes');
      
      // Aba 3: Medicamentos
      const medsData = (stats?.all_medications_list || []).map(m => ({
        'Medicamento': m.name,
        'Quantidade Prescrita': m.count
      }));
      const medsSheet = XLSX.utils.json_to_sheet(medsData);
      medsSheet['!cols'] = [{ wch: 50 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(workbook, medsSheet, 'Medicamentos');
      
      // Aba 4: Pacientes por Unidade
      const unitsData = (stats?.patients_by_unit || []).map(u => ({
        'Unidade de Saúde': u.unit_name,
        'Quantidade de Pacientes': u.count,
        'Porcentagem': `${((u.count / patients.length) * 100).toFixed(1)}%`
      }));
      const unitsSheet = XLSX.utils.json_to_sheet(unitsData);
      unitsSheet['!cols'] = [{ wch: 50 }, { wch: 20 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, unitsSheet, 'Por Unidade');
      
      // Aba 5: Faixa Etária
      const ageData = ageGroupData.map(a => ({
        'Faixa Etária': a.faixa,
        'Quantidade de Pacientes': a.quantidade,
        'Porcentagem': `${((a.quantidade / patients.length) * 100).toFixed(1)}%`
      }));
      const ageSheet = XLSX.utils.json_to_sheet(ageData);
      ageSheet['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, ageSheet, 'Faixa Etária');
      
      // Aba 6: Receitas por Mês
      const monthData = prescriptionsByMonth.map(m => ({
        'Mês': m.month,
        'Quantidade de Receitas': m.quantidade
      }));
      const monthSheet = XLSX.utils.json_to_sheet(monthData);
      monthSheet['!cols'] = [{ wch: 20 }, { wch: 25 }];
      XLSX.utils.book_append_sheet(workbook, monthSheet, 'Receitas por Mês');
      
      // Capturar gráficos como imagens
      const chartImages = [];
      
      if (ageChartRef.current) {
        const ageImg = await captureChartAsImage(ageChartRef);
        if (ageImg) chartImages.push({ name: 'grafico_faixa_etaria.png', data: ageImg });
      }
      
      if (medsChartRef.current) {
        const medsImg = await captureChartAsImage(medsChartRef);
        if (medsImg) chartImages.push({ name: 'grafico_medicamentos.png', data: medsImg });
      }
      
      if (monthChartRef.current) {
        const monthImg = await captureChartAsImage(monthChartRef);
        if (monthImg) chartImages.push({ name: 'grafico_receitas_mes.png', data: monthImg });
      }
      
      if (pieChartRef.current) {
        const pieImg = await captureChartAsImage(pieChartRef);
        if (pieImg) chartImages.push({ name: 'grafico_unidades.png', data: pieImg });
      }
      
      // Adicionar aba com referência aos gráficos
      if (chartImages.length > 0) {
        const chartsInfoData = [
          ['GRÁFICOS DO RELATÓRIO', ''],
          ['', ''],
          ['Os gráficos foram exportados como imagens separadas.', ''],
          ['Verifique o arquivo ZIP gerado junto com este Excel.', ''],
          ['', ''],
          ['Lista de Gráficos:', ''],
          ...chartImages.map((img, i) => [`${i + 1}. ${img.name}`, ''])
        ];
        const chartsSheet = XLSX.utils.aoa_to_sheet(chartsInfoData);
        chartsSheet['!cols'] = [{ wch: 50 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(workbook, chartsSheet, 'Gráficos Info');
      }
      
      // Gerar arquivo Excel
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Se tiver gráficos, criar um ZIP com Excel + imagens
      if (chartImages.length > 0) {
        // Importar JSZip dinamicamente
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        
        // Adicionar Excel ao ZIP
        zip.file(`relatorio_sistema_receitas_${new Date().toISOString().split('T')[0]}.xlsx`, excelBlob);
        
        // Adicionar imagens dos gráficos
        const graficosFolder = zip.folder('graficos');
        chartImages.forEach(img => {
          graficosFolder.file(img.name, img.data, { base64: true });
        });
        
        // Gerar e baixar ZIP
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `relatorio_completo_${new Date().toISOString().split('T')[0]}.zip`);
        toast.success('Relatório com gráficos exportado! (ZIP contendo Excel + imagens)');
      } else {
        // Se não tiver gráficos, baixar apenas o Excel
        saveAs(excelBlob, `relatorio_sistema_receitas_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Relatório Excel exportado com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar relatório');
    } finally {
      setExporting(false);
    }
  };

  // Exportar para Excel (simples, sem gráficos)
  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Aba 1: Estatísticas Gerais
    const statsData = [
      ['ESTATÍSTICAS GERAIS', ''],
      ['Data de Geração', new Date().toLocaleString('pt-BR')],
      ['', ''],
      ['Total de Pacientes', stats?.total_patients || 0],
      ['Receitas Este Mês', stats?.prescriptions_this_month || 0],
      ['Receitas a Vencer (30 dias)', stats?.expiring_soon || 0],
      ['Receitas Expiradas', stats?.expired_prescriptions || 0],
    ];
    const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
    XLSX.utils.book_append_sheet(workbook, statsSheet, 'Estatísticas');
    
    // Aba 2: Pacientes
    const patientsData = filteredPatients.map(p => ({
      'Nome': p.nome,
      'Data Nascimento': p.data_nascimento || 'N/A',
      'Idade': calculateAge(p.data_nascimento) || 'N/A',
      'Unidade': healthUnits.find(u => u.id === p.unit_id)?.nome || 'Sem unidade',
      'Data Cadastro': new Date(p.data_cadastro).toLocaleDateString('pt-BR')
    }));
    const patientsSheet = XLSX.utils.json_to_sheet(patientsData);
    XLSX.utils.book_append_sheet(workbook, patientsSheet, 'Pacientes');
    
    // Aba 3: Medicamentos
    const medsData = (stats?.all_medications_list || []).map(m => ({
      'Medicamento': m.name,
      'Quantidade Prescrita': m.count
    }));
    const medsSheet = XLSX.utils.json_to_sheet(medsData);
    XLSX.utils.book_append_sheet(workbook, medsSheet, 'Medicamentos');
    
    // Aba 4: Pacientes por Unidade
    const unitsData = (stats?.patients_by_unit || []).map(u => ({
      'Unidade de Saúde': u.unit_name,
      'Quantidade de Pacientes': u.count
    }));
    const unitsSheet = XLSX.utils.json_to_sheet(unitsData);
    XLSX.utils.book_append_sheet(workbook, unitsSheet, 'Por Unidade');
    
    // Aba 5: Faixa Etária
    const ageData = ageGroupData.map(a => ({
      'Faixa Etária': a.faixa,
      'Quantidade de Pacientes': a.quantidade
    }));
    const ageSheet = XLSX.utils.json_to_sheet(ageData);
    XLSX.utils.book_append_sheet(workbook, ageSheet, 'Faixa Etária');
    
    // Gerar arquivo
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `relatorio_sistema_receitas_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Relatório Excel exportado com sucesso!');
  };

  // Dados para gráfico de receitas por mês
  const getPrescriptionsByMonth = () => {
    const months = {};
    prescriptions.forEach(p => {
      // Handle both field names (Portuguese and English)
      const dateStr = p.data_criacao || p.created_at;
      if (!dateStr) return;
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return;
      
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      if (!months[key]) {
        months[key] = { month: label, quantidade: 0 };
      }
      months[key].quantidade++;
    });
    // Sort by date key and return last 6 months
    return Object.entries(months)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([_, data]) => data);
  };

  const prescriptionsByMonth = getPrescriptionsByMonth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)]">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="rounded-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <h1 className="text-4xl font-bold tracking-tight text-foreground">
                Relatórios
              </h1>
            </div>
            <p className="text-muted-foreground font-medium">
              Exporte dados do sistema para análise
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={exportCompleteReport}
              variant="outline"
              className="border-[#0F766E] text-[#0F766E] hover:bg-[#0F766E]/10 rounded-lg"
            >
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button
              onClick={exportToExcel}
              variant="outline"
              className="border-[#0F766E] text-[#0F766E] hover:bg-[#0F766E]/10 rounded-lg"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Dados
            </Button>
            <Button
              onClick={exportToExcelWithCharts}
              disabled={exporting}
              className="bg-[#0F766E] hover:bg-[#0D6660] text-white rounded-lg"
            >
              {exporting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Image className="w-4 h-4 mr-2" />
              )}
              {exporting ? 'Exportando...' : 'Excel + Gráficos'}
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pacientes</p>
                  <p className="text-2xl font-bold">{stats?.total_patients || 0}</p>
                </div>
                <Users className="w-8 h-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Medicamentos</p>
                  <p className="text-2xl font-bold">{stats?.total_unique_medications || 0}</p>
                </div>
                <Pill className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">A Vencer</p>
                  <p className="text-2xl font-bold text-orange-500">{stats?.expiring_soon || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Unidades</p>
                  <p className="text-2xl font-bold">{healthUnits.length}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Age Group Chart */}
        <Card className="mb-8" ref={ageChartRef}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Pacientes por Faixa Etária
                </CardTitle>
                <CardDescription>
                  Distribuição de pacientes em intervalos de 10 anos
                </CardDescription>
              </div>
              <Badge variant="secondary">
                {patients.length} pacientes total
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={ageGroupData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="faixa" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80}
                  fontSize={12}
                  tick={{ fill: '#666' }}
                />
                <YAxis 
                  tick={{ fill: '#666' }}
                  label={{ value: 'Qtd. Pacientes', angle: -90, position: 'insideLeft', fill: '#666' }}
                />
                <Tooltip 
                  formatter={(value) => [`${value} paciente(s)`, 'Quantidade']}
                  labelFormatter={(label) => `Faixa: ${label} anos`}
                />
                <Legend />
                <Bar dataKey="quantidade" name="Pacientes" radius={[4, 4, 0, 0]}>
                  {ageGroupData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={AGE_COLORS[index % AGE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Medications by Age Group Chart */}
        <Card className="mb-8" ref={medsChartRef}>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Pill className="w-5 h-5 text-purple-500" />
                  Medicamentos Mais Utilizados por Idade
                </CardTitle>
                <CardDescription>
                  Top 10 medicamentos mais prescritos por faixa etária
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Faixa Etária:</span>
                <Select
                  value={selectedAgeRange}
                  onValueChange={setSelectedAgeRange}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-12">0-12 anos</SelectItem>
                    <SelectItem value="13-18">13-18 anos</SelectItem>
                    <SelectItem value="19-40">19-40 anos</SelectItem>
                    <SelectItem value="41-60">41-60 anos</SelectItem>
                    <SelectItem value="60+">60+ anos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {medicationsByAgeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={medicationsByAgeData} 
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    type="number"
                    tick={{ fill: '#666' }}
                  />
                  <YAxis 
                    type="category"
                    dataKey="name"
                    tick={{ fill: '#666', fontSize: 11 }}
                    width={140}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value} prescrição(ões)`, 'Quantidade']}
                    labelFormatter={(label) => `${label}`}
                  />
                  <Bar 
                    dataKey="count" 
                    name="Prescrições" 
                    radius={[0, 4, 4, 0]}
                    fill="#8B5CF6"
                  >
                    {medicationsByAgeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={AGE_COLORS[index % AGE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12">
                <Pill className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">
                  Nenhum medicamento encontrado para a faixa etária {selectedAgeRange} anos
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* NEW: Prescriptions by Month Line Chart */}
        {prescriptionsByMonth.length > 0 && (
          <Card className="mb-8" ref={monthChartRef}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Receitas por Mês
                  </CardTitle>
                  <CardDescription>
                    Evolução de receitas emitidas nos últimos 6 meses
                  </CardDescription>
                </div>
                <Badge variant="secondary">
                  {prescriptions.length} receitas total
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={prescriptionsByMonth} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <defs>
                    <linearGradient id="colorReceitas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0F766E" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#0F766E" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fill: '#666' }} />
                  <YAxis tick={{ fill: '#666' }} />
                  <Tooltip 
                    formatter={(value) => [`${value} receita(s)`, 'Quantidade']}
                    labelFormatter={(label) => `Mês: ${label}`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="quantidade" 
                    stroke="#0F766E" 
                    fillOpacity={1} 
                    fill="url(#colorReceitas)" 
                    name="Receitas"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* NEW: Patients by Health Unit Pie Chart */}
        {stats?.patients_by_unit && stats.patients_by_unit.length > 0 && (
          <Card className="mb-8" ref={pieChartRef}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-500" />
                    Distribuição de Pacientes por Unidade
                  </CardTitle>
                  <CardDescription>
                    Porcentagem de pacientes cadastrados em cada unidade de saúde
                  </CardDescription>
                </div>
                <Badge variant="secondary">
                  {healthUnits.length} unidades
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={stats.patients_by_unit}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ unit_name, percent }) => `${unit_name.split(' ').slice(0, 2).join(' ')} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="unit_name"
                    >
                      {stats.patients_by_unit.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [`${value} paciente(s)`, props.payload.unit_name]}
                    />
                    <Legend 
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      formatter={(value, entry) => entry.payload?.unit_name?.substring(0, 25) || value}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Interactive Chart - Gráfico Personalizado */}
        <div className="mb-8">
          <InteractiveChart
            patients={patients}
            prescriptions={prescriptions}
            healthUnits={healthUnits}
            deliveryDates={deliveryDates}
            title="Análise Personalizada SUS"
            description="Configure os eixos X e Y para criar visualizações personalizadas dos dados da unidade"
          />
        </div>

        {/* NEW: Combined Medications Chart (Regular + Avulsa) */}
        {combinedMedsStats?.medications && combinedMedsStats.medications.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Pill className="w-5 h-5 text-emerald-500" />
                    Medicamentos Prescritos (Todas as Receitas)
                  </CardTitle>
                  <CardDescription>
                    Comparação entre receitas regulares e avulsas - Top 15 medicamentos
                  </CardDescription>
                </div>
                <div className="flex gap-4 text-sm">
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                    Regulares: {combinedMedsStats.summary?.total_regular_prescriptions || 0}
                  </Badge>
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    Avulsas: {combinedMedsStats.summary?.total_quick_prescriptions || 0}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={450}>
                <BarChart 
                  data={combinedMedsStats.medications.slice(0, 15)} 
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fill: '#666' }} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fill: '#666', fontSize: 11 }}
                    width={140}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${value} prescrição(ões)`, 
                      name === 'regular_count' ? 'Receitas Regulares' : 'Receitas Avulsas'
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="regular_count" name="Receitas Regulares" fill="#10B981" stackId="a" />
                  <Bar dataKey="avulsa_count" name="Receitas Avulsas" fill="#8B5CF6" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* NEW: Exams Statistics Chart */}
        {examsStats?.top_exams && examsStats.top_exams.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TestTube className="w-5 h-5 text-cyan-500" />
                    Exames Mais Solicitados
                  </CardTitle>
                  <CardDescription>
                    Top 15 exames mais solicitados no sistema
                  </CardDescription>
                </div>
                <Badge variant="secondary">
                  {examsStats.total_exam_requests || 0} solicitações total
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={examsStats.top_exams.slice(0, 15)} 
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 180, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fill: '#666' }} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fill: '#666', fontSize: 11 }}
                    width={170}
                  />
                  <Tooltip formatter={(value) => [`${value} solicitação(ões)`, 'Quantidade']} />
                  <Bar dataKey="count" name="Solicitações" fill="#06B6D4" radius={[0, 4, 4, 0]}>
                    {examsStats.top_exams.slice(0, 15).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Exam Statistics Section */}
        <Card className="mb-8 shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TestTube className="w-5 h-5 text-teal-600" />
              Estatísticas de Exames Solicitados
            </CardTitle>
            <CardDescription>
              Resumo dos exames mais solicitados no período
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ExamStatistics simplified={true} />
          </CardContent>
        </Card>

        {/* Export Options */}
        <Tabs defaultValue="patients" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-white border border-gray-100 rounded-lg p-1 mb-6">
            <TabsTrigger value="patients" className="rounded-md">
              <Users className="w-4 h-4 mr-2" />
              Pacientes
            </TabsTrigger>
            <TabsTrigger value="medications" className="rounded-md">
              <Pill className="w-4 h-4 mr-2" />
              Medicamentos
            </TabsTrigger>
            <TabsTrigger value="units" className="rounded-md">
              <Building2 className="w-4 h-4 mr-2" />
              Unidades
            </TabsTrigger>
          </TabsList>

          {/* Patients Tab */}
          <TabsContent value="patients">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Relatório de Pacientes</CardTitle>
                    <CardDescription>
                      {filteredPatients.length} paciente(s) encontrado(s)
                    </CardDescription>
                  </div>
                  <Button onClick={exportPatientsToCSV} className="rounded-full">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="text-xs mb-1 block">Buscar paciente</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={filters.search}
                        onChange={(e) => setFilters({...filters, search: e.target.value})}
                        placeholder="Nome do paciente..."
                        className="pl-10 h-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Unidade de Saúde</Label>
                    <Select
                      value={filters.unit_id}
                      onValueChange={(value) => setFilters({...filters, unit_id: value === 'all' ? '' : value})}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Todas as unidades" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as unidades</SelectItem>
                        {healthUnits.map(unit => (
                          <SelectItem key={unit.id} value={unit.id}>{unit.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      variant="outline" 
                      onClick={() => setFilters({unit_id: '', startDate: '', endDate: '', search: ''})}
                      className="rounded-full"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      Limpar Filtros
                    </Button>
                  </div>
                </div>

                {/* Patient Table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium text-muted-foreground">#</th>
                          <th className="text-left p-3 text-sm font-medium text-muted-foreground">Nome</th>
                          <th className="text-left p-3 text-sm font-medium text-muted-foreground">Data Nasc.</th>
                          <th className="text-left p-3 text-sm font-medium text-muted-foreground">Unidade</th>
                          <th className="text-left p-3 text-sm font-medium text-muted-foreground">Cadastro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPatients.map((patient, index) => (
                          <tr key={patient.id} className="border-t hover:bg-gray-50">
                            <td className="p-3 text-sm">{index + 1}</td>
                            <td className="p-3 text-sm font-medium">{patient.nome}</td>
                            <td className="p-3 text-sm">{patient.data_nascimento || 'N/A'}</td>
                            <td className="p-3 text-sm">
                              <Badge variant="outline">
                                {healthUnits.find(u => u.id === patient.unit_id)?.nome || 'Sem unidade'}
                              </Badge>
                            </td>
                            <td className="p-3 text-sm">
                              {new Date(patient.data_cadastro).toLocaleDateString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Medications Tab */}
          <TabsContent value="medications">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Relatório de Medicamentos</CardTitle>
                    <CardDescription>
                      {stats?.total_unique_medications || 0} medicamento(s) único(s)
                    </CardDescription>
                  </div>
                  <Button onClick={exportMedicationsToCSV} className="rounded-full">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium text-muted-foreground">#</th>
                          <th className="text-left p-3 text-sm font-medium text-muted-foreground">Medicamento</th>
                          <th className="text-right p-3 text-sm font-medium text-muted-foreground">Prescrições</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stats?.all_medications_list || []).map((med, index) => (
                          <tr key={index} className="border-t hover:bg-gray-50">
                            <td className="p-3 text-sm">{index + 1}</td>
                            <td className="p-3 text-sm font-medium">{med.name}</td>
                            <td className="p-3 text-sm text-right">
                              <Badge>{med.count}x</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Units Tab */}
          <TabsContent value="units">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Relatório por Unidade</CardTitle>
                    <CardDescription>
                      Distribuição de pacientes nas unidades de saúde
                    </CardDescription>
                  </div>
                  <Button onClick={exportPatientsByUnitToCSV} className="rounded-full">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {(stats?.patients_by_unit || []).map((unit, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{unit.unit_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {unit.count} paciente(s)
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-lg px-4 py-1">
                        {unit.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
